import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
// import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GridSearchIcon } from "@mui/x-data-grid";
import { isEqual, omit } from "lodash";
import { useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import Flex from "../../../components/Flex";
import MultiSelect from "../../../components/MultiSelect";
import CustomTabs from "../../../components/app/CustomTabs";
import CustomTitle from "../../../components/app/CustomTitle";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../components/app/DeleteModal";
import ProviderGroupProfileDetailCard from "../../../components/app/ProviderGroupProfileDetailCard";
import StyledButtonV2 from "../../../components/app/StyledButtonV2";
import AddPatient from "../../../components/app/admin/provider-groups/AddPatient";
import DepartmentDrawer from "../../../components/app/admin/provider-groups/DepartmentDrawer";
import LocationsDrawer from "../../../components/app/admin/provider-groups/LocationsDrawer";
import ProviderGroupDrawer from "../../../components/app/admin/provider-groups/ProviderGroupDrawer";
import UpsertProviderUserDrawer from "../../../components/app/admin/provider-groups/ProviderUserDrawer";
import UpsertStaffUserDrawer from "../../../components/app/admin/provider-groups/UpsertStaffUserDrawer";
import { STATE_LIST } from "../../../constants";
import { ProviderGroupSubmitValues } from "../../../constants/ProviderGroupFormInitialValues";
import {
  PROVIDER_GROUP_LOCATION_FORM_INITIAL_VALUES,
  ProviderGroupLocationFormType,
} from "../../../constants/ProviderLocationFromInitialValues";
import {
  PROVIDER_USER_INITIAL_VALUES,
  ProviderUserType,
} from "../../../constants/ProviderUserSchema";
import { PartialCustomTabData } from "../../../constants/constant-types";
import { StaffUser } from "../../../constants/staff-user-schema";
import { useDebounce } from "../../../hooks/use-debounce";
import { useReduceDisclosure } from "../../../hooks/use-reduce-disclosure";
import { useEditProviderGroupProfile } from "../../../queries/admin/provider-group/use-edit-provider-group-profile";
import { useProviderGroupProfile } from "../../../queries/admin/provider-group/use-provider-group-profile";
import { usePgAddLocation } from "../../../queries/use-add-location";
import { useAddProviderUser } from "../../../queries/use-add-provider-user";
import { useAddStaffUser } from "../../../queries/use-add-staff-user";
import { useArchiveProviderGroup } from "../../../queries/use-archieve-provider-group";
import { useChangeProviderGroupAvatar } from "../../../queries/use-change-avatar";
import {
  DEFAULT_FONTSIZE,
  DEFAULT_INS_LABEL_LIST,
  DEFAULT_UUID_NAME_LIST,
  getUnNecessaryTransformData,
  transformMultiSelectFrontEndVal,
  unNecessaryFEOfficeHourTrans,
} from "../../../utils/utils";

const SEARCH_PLACEHOLDER: Record<string, string> = {
  locations: "Search By Name",
  users: "Search By Name",
  providers: "Search By Name",
  patients: "Search By Name, Date of Birth,Contact Number and ID",
};

const ProviderGroupLayout = () => {
  const navigate = useNavigate();
  const [
    addPatientDisclosure,
    addDepartmentDisclosure,
    providerUserModalDisclosure,
    editProviderFormDisclosure,
    staffUserModalDisclosure,
    locationsModalDisclosure,
  ] = useReduceDisclosure(6);

  const params = useParams();
  const { mutateAsync: addLocation } = usePgAddLocation();
  const { mutateAsync: addStaffUser } = useAddStaffUser();

  const { mutateAsync: editProviderGroupProfile } =
    useEditProviderGroupProfile();
  const { mutateAsync: ChangeProviderGroupAvatar } =
    useChangeProviderGroupAvatar();
  const [search, setSearch, instantSearchVal] = useDebounce<string>("");
  const { uuid } = useParams();

  const { data: profileData }: any = useProviderGroupProfile(uuid);
  const { mutateAsync: addProviderUser } = useAddProviderUser();
  const deleteDisClosure = useDeleteDisClosure(false);
  const [stateFilter, setStateFilter] = useState<string[]>();

  const providerGroupsButtonData: PartialCustomTabData[] = [
    {
      value: "profile",
      path: `/app/provider-groups/${params.providerGroupId}/${params?.uuid}`,
      label: "Profile",
    },
    {
      value: "locations",
      path: `/app/provider-groups/${params.providerGroupId}/${params.uuid}/locations`,
      label: "Locations",
    },
    {
      value: "departments",
      path: `/app/provider-groups/${params.providerGroupId}/${params.uuid}/departments`,
      label: "Departments",
    },
    {
      value: "patients",
      path: `/app/provider-groups/${params.providerGroupId}/${params.uuid}/patients`,
      label: "Patients",
    },
    {
      value: "users",
      path: `/app/provider-groups/${params.providerGroupId}/${params.uuid}/users`,
      label: "Staffs",
    },
    {
      value: "providers",
      path: `/app/provider-groups/${params.providerGroupId}/${params.uuid}/providers`,
      label: "Providers",
    },
    {
      value: "configuration",
      path: `/app/provider-groups/${params.providerGroupId}/${params.uuid}/configuration`,
      label: "Configuration",
    },
  ];

  const [activeButton, setActiveButton] = useState<string>(
    (
      providerGroupsButtonData.find((ele) => {
        const regex =
          ele.value === "profile"
            ? new RegExp(`^/app/provider-groups/[\\w-]+/[\\w-]+$`)
            : new RegExp(`^/app/provider-groups/[\\w-]+/[\\w-]+/${ele.value}$`);
        return regex.test(location.pathname);
      }) ?? providerGroupsButtonData[0]
    )?.value
  );
  function onEditProviderGroupSubmit(values: ProviderGroupSubmitValues) {
    const Transformed = {
      ...profileData,
      officeHours: unNecessaryFEOfficeHourTrans(profileData?.officeHours),
      acceptedInsurances: transformMultiSelectFrontEndVal(
        DEFAULT_INS_LABEL_LIST,
        profileData?.acceptedInsurances as any
      ),
      specialities: transformMultiSelectFrontEndVal(
        DEFAULT_UUID_NAME_LIST,
        profileData?.specialities as any
      ),
    };

    const transFormData: any = getUnNecessaryTransformData(
      ["acceptedInsurances", "specialities"],
      values
    );

    return editProviderGroupProfile({
      providerGroup: {
        ...transFormData,
        uuid: params.uuid,
      },
    }).then((res) => {
      if (!values?.avatar?.includes(".amazonaws.com")) {
        return ChangeProviderGroupAvatar({
          providerGroupId: params.uuid ?? "",
          avatar: {
            newAvatar: values.avatar,
          },
        }).then((avatarResp) => {
          if (isEqual(omit(values, "avatar"), omit(Transformed, "avatar"))) {
            return avatarResp;
          } else return res;
        });
      }
      return res;
    });
  }
  const { mutateAsync: achieveProviderGroup } = useArchiveProviderGroup();

  async function onAddLocationSubmit(values: ProviderGroupLocationFormType) {
    const transFormData: any = getUnNecessaryTransformData(
      ["specialities"],
      values
    );

    return addLocation({
      ...transFormData,
      xTENANTID: params.providerGroupId,
    } as any);
  }

  async function onSubmitAddStaffUser(values: StaffUser) {
    const transFormData: any = getUnNecessaryTransformData(
      ["workLocations"],
      values
    );
    return addStaffUser({
      user: transFormData,
      xTENANTID: params.providerGroupId,
    });
  }

  function addProviderSubmitHandler(values: ProviderUserType) {
    /** data with addition transformation of multiselect data */
    const transFormData: any = getUnNecessaryTransformData(
      ["licensedStates", "specialities", "acceptedInsurances", "workLocations"],
      values
    );

    return addProviderUser({
      ...transFormData,
      xTENANTID: params.providerGroupId,
    });
  }

  return (
    <Box>
      <Flex flexDirection={"column"}>
        <Flex alignItems={"center"} width={"100%"}>
          <Flex
            onClick={() => navigate("/app/provider-groups")}
            alignItems={"center"}
            sx={{ cursor: "pointer" }}
          >
            <ArrowBackIcon sx={{ color: "primary.main" }} fontSize="medium" />
            <CustomTitle ml={"10px"} color="primary.main">
              Provider Group
            </CustomTitle>
          </Flex>
        </Flex>
        <Flex
          justifyContent={"space-between"}
          alignItems={"center"}
          flexWrap={"wrap"}
        >
          <CustomTabs
            style={{ height: "2.5rem" }}
            tabsData={providerGroupsButtonData}
            value={activeButton}
            onChange={(v) => {
              if (search !== "") setSearch("");
              setStateFilter(undefined);
              navigate(
                providerGroupsButtonData.find((val) => val.value === v)?.path ??
                  providerGroupsButtonData[0].path
              );
              setActiveButton(v);
            }}
          />

          <Flex gap={"30px"} justifyContent={"center"} alignItems={"center"}>
            {activeButton !== "profile" && activeButton !== "configuration" && (
              <TextField
                sx={{
                  width: "391px",
                  "& .MuiInputBase-input": DEFAULT_FONTSIZE,
                }}
                placeholder={
                  SEARCH_PLACEHOLDER[activeButton] ?? "Search By Names"
                }
                variant="outlined"
                value={instantSearchVal}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton>
                        <GridSearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {location.pathname ===
              `/app/provider-groups/${params.providerGroupId}/${params.uuid}` && (
              <>
                <StyledButtonV2
                  variant="outlined"
                  label="Edit Profile"
                  onClick={editProviderFormDisclosure.open}
                  startIcon={<ModeEditOutlineOutlinedIcon />}
                />

                <StyledButtonV2
                  variant="outlined"
                  disabled={profileData?.active ? !profileData?.archive : false}
                  label={!profileData?.archive ? "Archive" : "Unarchive"}
                  color="warning"
                  onClick={() => deleteDisClosure.open(params?.uuid)}
                  startIcon={<DeleteIcon />}
                />
              </>
            )}
            {/* pg group location detail */}
            {location.pathname ===
              `/app/provider-groups/${params.providerGroupId}/${params.uuid}/locations` && (
              <>
                <Box minWidth={"15rem"}>
                  <MultiSelect
                    placeholder="States"
                    value={stateFilter}
                    optionWithCheckbox
                    options={STATE_LIST}
                    onChange={(val) => setStateFilter(val)}
                  />
                </Box>
                <StyledButtonV2
                  label="Add Location"
                  onClick={locationsModalDisclosure.open}
                  shouldAddIcon
                />
              </>
            )}
            {/* department section */}
            {location.pathname ===
              `/app/provider-groups/${params.providerGroupId}/${params.uuid}/departments` && (
              <StyledButtonV2
                label="Add Department"
                onClick={addDepartmentDisclosure.open}
                shouldAddIcon
              />
            )}
            {/* patient route  detail section */}
            {location.pathname ===
              `/app/provider-groups/${params.providerGroupId}/${params.uuid}/patients` && (
              <StyledButtonV2
                label="Add Patient"
                onClick={addPatientDisclosure.open}
                shouldAddIcon
              />
            )}
            {/* user route section */}
            {/* {location.pathname ===
                `/app/provider-groups/${params.providerGroupId}/${params.uuid}/users` && (
                <PopupState variant="popover" popupId="user-button-menu">
                  {(menuList) => (
                    <Box>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        {...bindTrigger(menuList)}
                      >
                        <CustomTitle fontSize={"18"}>Add New User</CustomTitle>
                      </Button>
                      <Menu {...bindMenu(menuList)}>
                        <MenuItem onClick={staffUserModalDisclosure.open}>
                          <Typography width={"128px"}>Staff User</Typography>
                        </MenuItem>
                        <MenuItem onClick={providerUserModalDisclosure.open}>
                          <Typography width={"128px"}>Provider User</Typography>
                        </MenuItem>
                      </Menu>
                    </Box>
                  )}
                </PopupState>
              )} */}
            {location.pathname ===
              `/app/provider-groups/${params.providerGroupId}/${params.uuid}/users` && (
              <StyledButtonV2
                shouldAddIcon
                variant="contained"
                onClick={staffUserModalDisclosure.open}
                label="Add Staff User"
              />
            )}
            {location.pathname ===
              `/app/provider-groups/${params.providerGroupId}/${params.uuid}/providers` && (
              <StyledButtonV2
                shouldAddIcon
                variant="contained"
                onClick={providerUserModalDisclosure.open}
                label="Add Provider User"
              />
            )}
          </Flex>
        </Flex>
      </Flex>

      <DeleteModal
        w="380px"
        open={deleteDisClosure.opened}
        onClose={deleteDisClosure.close}
        id={deleteDisClosure?.deleteId}
        onConfirmation={(id) =>
          achieveProviderGroup({
            id: id ?? "",
            xTENANTID: params?.providerGroupId ?? "",
            status: !profileData?.archive,
          }).then((res) => {
            setTimeout(() => {
              navigate("/app/provider-groups");
            }, 1500);
            return res;
          })
        }
        content={`You want to ${
          profileData?.archive ? "Unarchive" : "Archive"
        } this Provider Group`}
      />

      {location.pathname !==
        `/app/provider-groups/${params.providerGroupId}/${params.uuid}` && (
        <Box marginY={"20px"}>
          <ProviderGroupProfileDetailCard profileData={profileData} />
        </Box>
      )}
      <AddPatient
        modalTitle="Upload"
        open={addPatientDisclosure.opened}
        onClose={addPatientDisclosure.close}
      />
      <LocationsDrawer
        providerGroupTimezone={profileData?.timezone}
        open={locationsModalDisclosure.opened}
        shouldGetPrefillList
        prefillSpecialtyList={transformMultiSelectFrontEndVal(
          DEFAULT_UUID_NAME_LIST,
          profileData?.specialities
        )}
        preFillValues={{
          ...PROVIDER_GROUP_LOCATION_FORM_INITIAL_VALUES,
          timezone: profileData?.timezone,
          officeHours: unNecessaryFEOfficeHourTrans(profileData?.officeHours),
        }}
        handleCloseModal={locationsModalDisclosure.close}
        title={`Add Location For ${profileData?.name}`}
        onSubmit={onAddLocationSubmit}
      />
      <DepartmentDrawer
        open={addDepartmentDisclosure.opened}
        modalTitle={"Add Department"}
        onClose={addDepartmentDisclosure.close}
        xTENANTID={params.providerGroupId}
      />

      <UpsertStaffUserDrawer
        open={staffUserModalDisclosure.opened}
        modalTitle="Add Staff User"
        onClose={staffUserModalDisclosure.close}
        onSubmit={onSubmitAddStaffUser}
      />
      <UpsertProviderUserDrawer
        prefillSpecialtyList={transformMultiSelectFrontEndVal(
          DEFAULT_UUID_NAME_LIST,
          profileData?.specialities
        )?.map((it) => ({ uuid: it?.uuid, name: it?.name }))}
        shouldGetPrefillList
        open={providerUserModalDisclosure.opened}
        title="Add Provider User"
        onClose={providerUserModalDisclosure.close}
        onSubmit={addProviderSubmitHandler}
        prefillValues={{
          ...(PROVIDER_USER_INITIAL_VALUES ?? {}),
          timezone: profileData?.timezone,
          groupNpiNumber: profileData?.npi,
        }}
      />

      <ProviderGroupDrawer
        openProviderForm={editProviderFormDisclosure.opened}
        handleClose={editProviderFormDisclosure.close}
        title="Edit Provider Group Profile"
        onSubmit={onEditProviderGroupSubmit}
        prefillValues={
          {
            ...profileData,
            officeHours: unNecessaryFEOfficeHourTrans(profileData?.officeHours),
            acceptedInsurances: transformMultiSelectFrontEndVal(
              DEFAULT_INS_LABEL_LIST,
              profileData?.acceptedInsurances as any
            ),
            specialities: transformMultiSelectFrontEndVal(
              DEFAULT_UUID_NAME_LIST,
              profileData?.specialities as any
            ),
          } as any
        }
      />

      <Flex minHeight={"calc(100svh - 35svh)"}>
        <Outlet
          context={{
            searchKey: search,
            stateFilter: stateFilter,
            groupNpi: profileData?.npi,
            pgName: profileData?.name,
            profileData,
          }}
        />
      </Flex>
    </Box>
  );
};

export default ProviderGroupLayout;
